import { 
  ConfigApi,
  DiscoveryApi,
  FetchApi, 
  IdentityApi,
  createApiRef
} from '@backstage/core-plugin-api';
import { 
  TerraformCloudApi, 
  TFCOrganization,
  TFCProject,
  TFCWorkspace,
  TFCRun,
  TFCApiResponse,
  TFCTokenInfo
} from './types';

export const terraformCloudApiRef = createApiRef<TerraformCloudApi>({
  id: 'plugin.terraform-cloud.api',
});

/**
 * Options for creating a TerraformCloudClient
 */
export interface TerraformCloudClientOptions {
  configApi: ConfigApi;
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  identityApi: IdentityApi;
}

/**
 * Default implementation of the TerraformCloudApi
 */
export class TerraformCloudClient implements TerraformCloudApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;
  private defaultOrganization: string | null = null;

  constructor(options: TerraformCloudClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('terraform-cloud');
  }

  private async getHeaders(): Promise<HeadersInit> {
    const { token } = await this.identityApi.getCredentials();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async getAvailableTokens(): Promise<TFCTokenInfo[]> {
    try {
      const baseUrl = await this.getBaseUrl();
      const headers = await this.getHeaders();
      const response = await this.fetchApi.fetch(`${baseUrl}/available-tokens`, { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch available tokens');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get available tokens:', error);
      return [];
    }
  }

  private async makeRequest<T>(
    path: string,
    _organization?: string,
    options?: RequestInit
  ): Promise<T> {
    // Use the backend proxy to avoid CORS issues
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();
    
    // Remove leading slash from path if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const proxyUrl = `${baseUrl}/proxy/${cleanPath}`;
    
    const response = await this.fetchApi.fetch(proxyUrl, {
      ...options,
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `TFC API error (${response.status})`;
      
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) {
          errorMessage = errorJson.error;
          if (errorJson.searchedPaths) {
            errorMessage += `. Searched paths: ${errorJson.searchedPaths.join(', ')}`;
          }
        } else if (errorJson.errors && Array.isArray(errorJson.errors)) {
          errorMessage = errorJson.errors.map((e: any) => e.detail || e.title).join(', ');
        }
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    return await response.json();
  }

  async getOrganizations(): Promise<TFCOrganization[]> {
    try {
      const response = await this.makeRequest<TFCApiResponse<TFCOrganization[]>>(
        '/organizations'
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      throw error;
    }
  }

  async getProjects(orgName: string): Promise<TFCProject[]> {
    try {
      const response = await this.makeRequest<TFCApiResponse<TFCProject[]>>(
        `/organizations/${orgName}/projects`,
        orgName
      );
      
      // Fetch additional metadata for projects
      const projectsWithMeta = await Promise.all(
        response.data.map(async (project) => {
          try {
            // Get workspaces for this project to calculate metadata
            const workspaces = await this.getWorkspaces(orgName, project.id);
            
            let lastRunAt: string | undefined;
            let lastRunStatus: string | undefined;
            
            // Find the most recent run across all workspaces
            for (const workspace of workspaces) {
              if (workspace.relationships['latest-run']?.data?.id) {
                try {
                  const run = await this.getRun(workspace.relationships['latest-run'].data.id);
                  const runDate = new Date(run.attributes['created-at']);
                  
                  if (!lastRunAt || runDate > new Date(lastRunAt)) {
                    lastRunAt = run.attributes['created-at'];
                    lastRunStatus = run.attributes.status;
                  }
                } catch {
                  // Ignore errors fetching individual runs
                }
              }
            }
            
            return {
              ...project,
              meta: {
                'workspace-count': workspaces.length,
                'last-run-at': lastRunAt,
                'last-run-status': lastRunStatus,
              },
            };
          } catch {
            // Return project without meta if we can't fetch workspaces
            return project;
          }
        })
      );
      
      // Sort by last run date (most recent first)
      return projectsWithMeta.sort((a, b) => {
        const dateA = a.meta?.['last-run-at'] ? new Date(a.meta['last-run-at']).getTime() : 0;
        const dateB = b.meta?.['last-run-at'] ? new Date(b.meta['last-run-at']).getTime() : 0;
        return dateB - dateA;
      });
    } catch (error) {
      console.error(`Failed to fetch projects for organization ${orgName}:`, error);
      throw error;
    }
  }

  async getWorkspaces(orgName: string, projectId?: string): Promise<TFCWorkspace[]> {
    try {
      let url = `/organizations/${orgName}/workspaces`;
      if (projectId) {
        url += `?filter[project][id]=${projectId}`;
      }
      
      const response = await this.makeRequest<TFCApiResponse<TFCWorkspace[]>>(
        url,
        orgName
      );
      
      // Sort by updated-at (most recent first)
      return response.data.sort((a, b) => {
        const dateA = new Date(a.attributes['updated-at']).getTime();
        const dateB = new Date(b.attributes['updated-at']).getTime();
        return dateB - dateA;
      });
    } catch (error) {
      console.error(`Failed to fetch workspaces for organization ${orgName}:`, error);
      throw error;
    }
  }

  async getWorkspaceRuns(workspaceId: string, limit: number = 10): Promise<TFCRun[]> {
    try {
      const response = await this.makeRequest<TFCApiResponse<TFCRun[]>>(
        `/workspaces/${workspaceId}/runs?page[size]=${limit}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch runs for workspace ${workspaceId}:`, error);
      throw error;
    }
  }

  async getRunStateVersion(runId: string): Promise<string | null> {
    try {
      // Fetch the run's apply details which includes the state version
      const response = await this.makeRequest<any>(
        `/runs/${runId}`
      );
      
      // Check if this run has an apply with state-output
      if (response.data?.relationships?.apply?.data?.id) {
        // The state version ID is typically referenced in the apply
        // For now, we'll return null and use workspace's current state version
        return null;
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch state version for run ${runId}:`, error);
      return null;
    }
  }

  async getRun(runId: string): Promise<TFCRun> {
    try {
      const response = await this.makeRequest<TFCApiResponse<TFCRun>>(
        `/runs/${runId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch run ${runId}:`, error);
      throw error;
    }
  }

  setDefaultOrganization(orgName: string): void {
    this.defaultOrganization = orgName;
    localStorage.setItem('terraform-cloud-default-org', orgName);
  }

  getDefaultOrganization(): string | null {
    if (!this.defaultOrganization) {
      this.defaultOrganization = localStorage.getItem('terraform-cloud-default-org');
    }
    return this.defaultOrganization;
  }
}